import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from "../common/Loader.js"
import Alert from '../common/Alert.js';
import contractPartyService from '../../services/contractPartyService.js';
import { ArrowLeft, Edit3, Trash2, Users } from 'lucide-react';
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';


const ViewContractParty = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { partyId } = useParams();
  const [loading, setLoading] = useState(true);
  const [party, setParty] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    const fetchParty = async () => {
      try {
        const response = await contractPartyService.getById(partyId);
        setParty(response.data);
      } catch (err) {
        Alert.error('Failed to load contract party');
        console.error(err);
        navigate('/contract-parties');
      } finally {
        setLoading(false);
      }
    };

    fetchParty();
  }, [partyId, navigate]);

  const handleDelete = async () => {
    try {
      await contractPartyService.delete(partyId);
      Alert.success('Contract party deleted successfully!');
      navigate('/contract-parties');
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to delete contract party');
      setShowDeleteConfirm(false);
    }
  };

  const capitalizeStatus = (status) => {
    return status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : '';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading contract party details..." />
        </div>
      </DashboardLayout>
    );
  }

  if (!party) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-gray-600 font-semibold">Contract party not found</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col gap-3 lg:flex-row lg:justify-between lg:items-center mb-4 sm:mb-6">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-700 flex flex-wrap items-center gap-2 sm:gap-3">
            <Users className="w-6 h-6 text-buttonhoverTxt shrink-0" />
            {party.name}
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">Contract Party Details</p>
        </div>
        <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3 w-full lg:w-auto">
          <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate('/contract-parties')}>
            Back to List
          </Button>
          <Button variant="primary" icon={Edit3} onClick={() => navigate(`/contract-parties/edit/${partyId}`)}>
            Edit
          </Button>
          <Button variant="danger" icon={Trash2} onClick={() => setShowDeleteConfirm(true)}>
            Delete
          </Button>
        </div>
      </div>

      {/* Status cards */}
      <div className="grid grid-cols-2 sm:grid-cols-2 xl:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
        <div className="bg-white rounded-xl shadow-md p-3 sm:p-5 border-l-4 border-orange-500">
          <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">Contact</p>
          <p className="text-base sm:text-lg font-bold text-gray-900 break-words">{party.contact}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-3 sm:p-5 border-l-4 border-blue-500">
          <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">Email</p>
          <p className="text-sm sm:text-base font-bold text-gray-900 break-all">{party.email}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-3 sm:p-5 border-l-4 border-green-500">
          <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">{t('common.company')}</p>
          <p className="text-base sm:text-lg font-bold text-gray-900 break-words">{party.company_name}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-3 sm:p-5 border-l-4 border-purple-500">
          <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">Status</p>
          <span className={`inline-block px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-bold border ${
            party.status === 'active' ? 'bg-green-100 text-green-700 border-green-200' : 'bg-red-100 text-red-700 border-red-200'
          }`}>
            {capitalizeStatus(party.status)}
          </span>
        </div>
      </div>

      {/* Details sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-bold text-gray-700 mb-4 pb-2 border-b border-gray-100">Basic Information</h2>
          <div className="grid grid-cols-2 gap-2 sm:gap-4">
            <div>
              <p className="text-xs text-gray-500 uppercase font-semibold tracking-wide">Full Name</p>
              <p className="text-sm sm:text-base font-medium text-gray-800 mt-1">{party.name}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase font-semibold tracking-wide">GST Number</p>
              <p className="text-sm sm:text-base font-medium text-gray-800 mt-1">{party.gst_number || '-'}</p>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <p className="text-xs text-gray-500 uppercase font-semibold tracking-wide">{t('common.createdBy')}</p>
              <p className="text-sm sm:text-base font-medium text-gray-800 mt-1">{party.created_by}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-bold text-gray-700 mb-4 pb-2 border-b border-gray-100">Additional Details</h2>
          <div className="grid grid-cols-2 gap-2 sm:gap-4">
            <div>
              <p className="text-xs text-gray-500 uppercase font-semibold tracking-wide">Created Date</p>
              <p className="text-sm sm:text-base font-medium text-gray-800 mt-1">{formatDate(party.created_at)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase font-semibold tracking-wide">Last Updated</p>
              <p className="text-sm sm:text-base font-medium text-gray-800 mt-1">{formatDate(party.updated_at)}</p>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <p className="text-xs text-gray-500 uppercase font-semibold tracking-wide">Party ID</p>
              <p className="text-sm sm:text-base font-medium text-gray-800 mt-1">#{party.id}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <h2 className="text-base sm:text-lg font-bold text-gray-700 mb-4 pb-2 border-b border-gray-100">{t('common.address')}</h2>
        <p className="text-sm sm:text-base font-medium text-gray-800 whitespace-pre-wrap break-words">{party.address}</p>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-xl shadow-lg max-w-sm w-full mx-4">
            <h2 className="text-lg font-bold text-gray-800 mb-4">Delete Contract Party</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{party.name}"? This action cannot be undone.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)} className="w-full sm:w-auto">
                Cancel
              </Button>
              <Button variant="danger" onClick={handleDelete} className="w-full sm:w-auto">
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default ViewContractParty;
